import 'react';
import Select$1 from 'react-select';
import styled, { css, withTheme } from 'styled-components';
import getColour from '@lendi-ui/commons/colours';
import getTypography from '@lendi-ui/commons/typography';
import { getRadius } from '@lendi-ui/commons/borders';
import { map } from '@lendi-ui/breakpoint';
import { normalise, deriveSize } from '@lendi-ui/utils';
import Close from '@lendi-ui/icon/Close';
import KeyboardArrowDown from '@lendi-ui/icon/KeyboardArrowDown';
import { pl, pr, pt, pb, ml } from '@lendi-ui/spacing';
import { jsx, jsxs } from 'react/jsx-runtime';
import Spinner from '@lendi-ui/spinner';
import ExpandMore from '@lendi-ui/icon/ExpandMore';
import { depth } from '@lendi-ui/depth';

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

var SelectWrapper = styled.span.withConfig({
  displayName: "indexstyle__SelectWrapper",
  componentId: "lui__sc-1cj2fhl-0"
})(["", ";"], normalise);
var LUISelectStyles = (theme, size, isInverse, isFullWidth, isError, isDisabled, isOptionsOverflow) => ({
  // the dropdown
  control: defaultStyles => {
    return _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
      // default comes from react-select
      borderRadius: getRadius()
    }, luiControlStyles(theme, size, isInverse, isFullWidth, isError, isDisabled));
  },
  // dropdown container
  container: defaultStyles => {
    return _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
      fontFamily: getTypography('body1', 'fontFamily'),
      width: isFullWidth ? '100%' : widthBySizeMixin(size)
    }, fontBySizeMixin(size));
  },
  // the text input
  input: defaultStyles => {
    return _objectSpread2(_objectSpread2(_objectSpread2({}, defaultStyles), {}, {
      color: isInverse ? 'white' : 'inherit'
    }, fontBySizeMixin(size)), {}, {
      '&:disabled': {
        cursor: 'not-allowed',
        backgroundColor: isInverse ? 'transparent' : getColour('fill', 'disabled')
      }
    });
  },
  // single value being selection
  singleValue: defaultStyles => {
    return _objectSpread2(_objectSpread2({}, defaultStyles), fontBySizeMixin(size));
  },
  // multiple value label
  multiValueLabel: defaultStyles => {
    return _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
      fontSize: '100%'
    });
  },
  // multiple value box
  multiValue: defaultStyles => {
    return _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
      backgroundColor: getColour('fill', 'selected'),
      borderRadius: getRadius()
    });
  },
  // multiple value cross icon
  multiValueRemove: defaultStyles => _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
    borderRadius: getRadius(),
    ':hover': {
      backgroundColor: getColour('shade', 300),
      color: 'white',
      cursor: 'pointer'
    }
  }),
  placeholder: defaultStyles => _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
    color: getColour('text', 'subtle')
  }),
  clearIndicator: (defaultStyles, _ref) => {
    return _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
      cursor: 'pointer',
      color: 'inherit',
      padding: '0 8px 0 0'
    });
  },
  dropdownIndicator: defaultStyles => _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
    cursor: 'pointer',
    color: 'inherit',
    padding: '0px 8px 0 4px'
  }),
  option: (defaultStyles, _ref2) => {
    var {
      isFocused,
      isSelected
    } = _ref2;
    var backgroundColor = isSelected ? getColour('fill', 'selected') : isFocused ? getColour('shade', 25) : 'transparent';
    var overflowCss = isOptionsOverflow ? {} : {
      maxHeight: '48px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    };
    var weight = isSelected ? '700' : undefined;
    return _objectSpread2(_objectSpread2(_objectSpread2({}, defaultStyles), {}, {
      color: getColour('text', 'main'),
      fontWeight: weight,
      wordWrap: 'break-word',
      paddingLeft: pl('sm')[1],
      paddingRight: pr('sm')[1],
      paddingTop: pt('xs')[1],
      paddingBottom: pb('xs')[1],
      backgroundColor,
      cursor: isDisabled ? 'default' : 'pointer'
    }, overflowCss), {}, {
      ':active': {
        backgroundColor: getColour('shade', 25)
      }
    });
  },
  menu: defaultStyles => _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
    paddingTop: pt('xxs')[1],
    paddingBottom: pb('xxs')[1],
    margin: 0,
    borderRadius: getRadius(),
    fontFamily: getTypography('body1', 'fontFamily')
  }),
  menuList: defaultStyles => _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
    backgroundColor: getColour('shade', 0),
    margin: 0,
    maxHeight: '192px',
    overflow: 'auto',
    outline: 'none'
  }),
  groupHeading: defaultStyles => _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
    textTransform: 'uppercase',
    paddingTop: '4px',
    fontSize: '90%',
    height: '25px'
  }),
  menuPortal: defaultStyles => _objectSpread2(_objectSpread2({}, defaultStyles), {}, {
    zIndex: 30 // greater than modal's one
  })
});

var luiControlStyles = (theme, size, isInverse, isFullWidth, isError, isDisabled) => {
  var styles;
  if (isDisabled) {
    styles = _objectSpread2(_objectSpread2({
      cursor: 'not-allowed',
      backgroundColor: isInverse ? 'transparent' : getColour('fill', 'disabled'),
      borderColor: isInverse ? getColour('shade', 0) : getColour('border', 'interaction')
    }, heightBySizeMixin(size)), {}, {
      width: isFullWidth ? '100%' : widthBySizeMixin(size)
    });
  } else {
    styles = _objectSpread2(_objectSpread2({
      borderWidth: "1px",
      borderColor: InputBorderColor({
        isInverse,
        isError,
        theme
      }),
      borderStyle: 'solid',
      backgroundColor: isInverse ? 'transparent' : getColour('fill', 'main')
    }, heightBySizeMixin(size)), {}, {
      width: isFullWidth ? '100%' : widthBySizeMixin(size)
    });
  }
  return styles;
};
var InputBorderColor = _ref3 => {
  var {
    isInverse,
    isError,
    theme
  } = _ref3;
  if (isError) return getColour('interaction', 'error');
  if (isInverse) return getColour('shade', 25);
  return getColour('border', 'interaction');
};
var heightBySizeMixin = size => {
  return map(size, val => {
    switch (val) {
      case 'xs':
        return {
          minHeight: deriveSize(1.625),
          borderRadius: '13px'
        };
      case 'sm':
        return {
          minHeight: deriveSize(2.5)
        };
      case 'md':
        return {
          minHeight: deriveSize(3)
        };
      case 'lg':
        return {
          minHeight: deriveSize(3.875)
        };
      default:
        return undefined;
    }
  });
};
var KeyboardArrowDownIcon = styled(KeyboardArrowDown).withConfig({
  displayName: "indexstyle__KeyboardArrowDownIcon",
  componentId: "lui__sc-1cj2fhl-1"
})(["color:", ";transition:transform 0.5s ease 0s;", ";", ";"], _ref4 => {
  var {
    theme
  } = _ref4;
  return theme.color.interaction.main;
}, _ref5 => {
  var {
    isOpen
  } = _ref5;
  return css(["transform:rotate(", ");"], isOpen ? '180deg' : '0deg');
}, _ref6 => {
  var {
    size
  } = _ref6;
  return iconBySizeMixin$1(size);
});
var CloseIcon = styled(Close).withConfig({
  displayName: "indexstyle__CloseIcon",
  componentId: "lui__sc-1cj2fhl-2"
})(["", ";"], _ref7 => {
  var {
    size
  } = _ref7;
  return iconBySizeMixin$1(size);
});
var iconBySizeMixin$1 = size => map(size, val => {
  switch (val) {
    case 'xs':
      return css(["top:", ";width:", ";height:", ";"], deriveSize(0.25), deriveSize(0.875), deriveSize(0.875));
    case 'sm':
      return css(["top:", ";width:", ";height:", ";"], deriveSize(0.35), deriveSize(1), deriveSize(1));
    case 'md':
      return css(["top:", ";width:", ";height:", ";"], deriveSize(0.45), deriveSize(1.25), deriveSize(1.25));
    case 'lg':
      return css(["top:", ";width:", ";height:", ";"], deriveSize(0.35), deriveSize(1.75), deriveSize(1.75));
    default:
      return undefined;
  }
});
var fontBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
      return {
        fontSize: deriveSize(0.7857)
      };
    case 'sm':
      return {
        fontSize: deriveSize(0.875)
      };
    case 'md':
      return {
        fontSize: deriveSize(1)
      };
    case 'lg':
      return {
        fontSize: deriveSize(1.125)
      };
    default:
      return undefined;
  }
});
var widthBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
      return deriveSize(12.5);
    case 'sm':
      return deriveSize(14.5);
    case 'lg':
      return deriveSize(24);
    case 'md':
    default:
      return deriveSize(21.5);
  }
});

var _excluded$2 = ["ref"];
var ClearIndicator = props => {
  var {
      children = /*#__PURE__*/jsx(CloseIcon, {
        color: getColour('interaction', 'main'),
        size: props.selectProps.size,
        "data-testid": "clearIcon"
      }),
      getStyles,
      innerProps: {
        ref
      }
    } = props,
    restInnerProps = _objectWithoutProperties(props.innerProps, _excluded$2);
  return /*#__PURE__*/jsx("div", _objectSpread2(_objectSpread2({}, restInnerProps), {}, {
    ref: ref,
    style: getStyles('clearIndicator', props),
    children: children
  }));
};
var DropdownIndicator = props => {
  var {
    children = /*#__PURE__*/jsx(KeyboardArrowDownIcon, {
      color: getColour('interaction', 'main'),
      size: props.selectProps.size,
      isOpen: props.selectProps.menuIsOpen,
      "data-testid": "dropDownIcon"
    }),
    getStyles
  } = props;
  return /*#__PURE__*/jsx("div", {
    style: getStyles('dropdownIndicator', props),
    children: children
  });
};

var _excluded$1 = ["theme", "isMultiple", "size", "isFullWidth", "isInverse", "isDisabled", "isError", "isMenuOpen", "isAutoFocus", "onChangeItem", "isClearableByBackspace", "isOptionsOverflow", "menuPortalTarget"];
var LUISelect = _ref => {
  var {
      theme,
      isMultiple = false,
      size = 'md',
      isFullWidth = false,
      isInverse = false,
      isDisabled = false,
      isError = false,
      isMenuOpen,
      isAutoFocus = false,
      onChangeItem = () => {},
      isClearableByBackspace = false,
      isOptionsOverflow = false,
      menuPortalTarget
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded$1);
  return /*#__PURE__*/jsx(SelectWrapper, {
    children: /*#__PURE__*/jsx(Select$1, _objectSpread2(_objectSpread2({}, otherProps), {}, {
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      styles: LUISelectStyles(theme, size, isInverse, isFullWidth, isError, isDisabled, isOptionsOverflow),
      isDisabled: isDisabled,
      isMulti: isMultiple,
      menuIsOpen: isMenuOpen,
      autoFocus: isAutoFocus,
      onChange: onChangeItem,
      backspaceRemovesValue: isClearableByBackspace,
      components: {
        ClearIndicator,
        DropdownIndicator
      },
      menuPortalTarget: menuPortalTarget !== null && menuPortalTarget !== void 0 ? menuPortalTarget : null // React-select exposes a menuPortalTarget prop, that lets you portal the select menu to a dom node of your choosing,
      // this is specifically useful when you use select inside modal, `see select-modal-example`
    }))
  });
};

var index = withTheme(LUISelect);

var dropBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'sm':
      return css(["padding:", " ", " ", " ", ";font-size:", ";"], deriveSize(0.37), deriveSize(2.14), deriveSize(0.37), deriveSize(0.57), deriveSize(0.8));
    case 'md':
      return css(["padding:", " ", " ", " ", ";font-size:", ";"], deriveSize(0.4), deriveSize(2.5), deriveSize(0.4), deriveSize(0.66), deriveSize(1));
    case 'lg':
      return css(["padding:", " ", " ", " ", ";font-size:", ";"], deriveSize(0.53), deriveSize(3.4), deriveSize(0.53), deriveSize(0.83), deriveSize(1.125));
    default:
      return undefined;
  }
});
var iconBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'sm':
      return css(["top:", ";width:", ";height:", ";"], deriveSize(0.35), deriveSize(1), deriveSize(1));
    case 'md':
      return css(["top:", ";width:", ";height:", ";"], deriveSize(0.45), deriveSize(1.25), deriveSize(1.25));
    case 'lg':
      return css(["top:", ";width:", ";height:", ";"], deriveSize(0.35), deriveSize(1.75), deriveSize(1.75));
    default:
      return undefined;
  }
});
var DropdownWrapper = styled.div.withConfig({
  displayName: "indexstyle__DropdownWrapper",
  componentId: "lui__sc-ebogep-0"
})(["", ";display:inline-block;position:relative;", " &:hover{", ";}"], normalise, _ref => {
  var {
    isFullWidth
  } = _ref;
  if (isFullWidth) {
    return css(["width:100%;"]);
  }
  return css(["", ";"], ml('xxs'));
}, _ref2 => {
  var {
    isDisabled
  } = _ref2;
  if (isDisabled) {
    return undefined;
  }
  return css(["", ";"], depth(4));
});
var Select = styled.select.withConfig({
  displayName: "indexstyle__Select",
  componentId: "lui__sc-ebogep-1"
})(["border-radius:4px 4px 0 0;font-family:", ";appearance:none;", " ", ";width:100%;", ";"], getTypography('body1', 'fontFamily'), _ref3 => {
  var {
    disabled
  } = _ref3;
  if (disabled) {
    return css(["cursor:not-allowed;"]);
  }
  return css(["cursor:pointer;"]);
}, _ref4 => {
  var {
    selectSize
  } = _ref4;
  return dropBySizeMixin(selectSize);
}, _ref5 => {
  var {
    isError
  } = _ref5;
  return isError ? css(["border:1px solid ", ";"], getColour('interaction', 'error')) : 'border: none;';
});
var IconDown = styled(ExpandMore).withConfig({
  displayName: "indexstyle__IconDown",
  componentId: "lui__sc-ebogep-2"
})(["position:absolute;right:6px;pointer-events:none;", ";"], _ref6 => {
  var {
    size
  } = _ref6;
  return iconBySizeMixin(size);
});
var SpinnerWrapper = styled(Spinner).withConfig({
  displayName: "indexstyle__SpinnerWrapper",
  componentId: "lui__sc-ebogep-3"
})(["position:absolute;right:6px;top:10px;", ";"], _ref7 => {
  var {
    size
  } = _ref7;
  return iconBySizeMixin(size);
});

var _excluded = ["size", "items", "isFullWidth", "isError", "isDisabled", "isLoading", "value", "onChange", "onFocus", "onBlur", "className", "name", "isMultiple"];
var NativeSelect = _ref => {
  var {
      size = 'md',
      items,
      isFullWidth = true,
      isError = false,
      isDisabled = false,
      isLoading = false,
      value,
      onChange: _onChange = () => undefined,
      onFocus: _onFocus = () => undefined,
      onBlur: _onBlur = () => undefined,
      className,
      name,
      isMultiple
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  // React requires value prop to be undefined or empty string if not provided
  value = value || undefined;
  isDisabled = isLoading ? true : isDisabled;
  return /*#__PURE__*/jsxs(DropdownWrapper, {
    isFullWidth: isFullWidth,
    isDisabled: isDisabled,
    className: className,
    children: [/*#__PURE__*/jsx(Select, _objectSpread2(_objectSpread2({}, otherProps), {}, {
      multiple: isMultiple,
      selectSize: size,
      isError: isError,
      disabled: isDisabled,
      value: value,
      onChange: e => _onChange(e.target.value),
      onFocus: e => _onFocus(e.target.value),
      onBlur: e => _onBlur(e.target.value),
      name: name,
      children: items.map(item => /*#__PURE__*/jsx("option", {
        value: item.value,
        disabled: item.isDisabled || false,
        hidden: item.isHidden || false,
        children: item.label
      }, item.value))
    })), isLoading ? /*#__PURE__*/jsx(SpinnerWrapper, {
      size: size,
      variant: "dark"
    }) : /*#__PURE__*/jsx(IconDown, {
      color: getColour('interaction', 'main'),
      size: size
    })]
  });
};

export { NativeSelect, index as Select };
